"use client";

import { useState, useEffect, useRef } from "react";
import { X, Loader2, Gift, ArrowRight, Check } from "lucide-react";
import { interceptIfPaymentsPaused } from "@/components/billing/payments-paused-store";
import {
  isCustomCheckoutEnabled,
  customCheckoutHref,
} from "@/lib/billing/checkout-mode";
import { isWhop } from "@/lib/payments/provider";
import {
  trackUpgradeEntrypointViewed,
  trackUpgradeEntrypointClicked,
} from "@/lib/analytics";

interface AnnualPromptData {
  eligible: boolean;
  plan?: string;
  monthlyPrice?: number;
  annualPrice?: number;
  annualMonthly?: number;
  savings?: number;
}

export function AnnualUpgradePrompt() {
  const [data, setData] = useState<AnnualPromptData | null>(null);
  const [dismissed, setDismissed] = useState(false);
  const [switching, setSwitching] = useState(false);
  const [switched, setSwitched] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetch("/api/billing/annual-prompt")
      .then((r) => r.json())
      .then((d) => setData(d))
      .catch(() => {});
  }, []);

  const isVisible = !!data?.eligible && !dismissed && !switched;

  // Fire the impression once, when the prompt first becomes visible.
  const viewedRef = useRef(false);
  useEffect(() => {
    if (!isVisible || viewedRef.current) return;
    viewedRef.current = true;
    trackUpgradeEntrypointViewed({
      entry_point: "annual_upgrade_prompt",
      surface: "workspace",
      plan_before: data?.plan ?? null,
    });
  }, [isVisible, data?.plan]);

  if (!data?.eligible || dismissed || switched) return null;

  const yearlyIfMonthly = (data.monthlyPrice ?? 0) * 12;

  async function handleSwitch() {
    trackUpgradeEntrypointClicked({
      entry_point: "annual_upgrade_prompt",
      surface: "workspace",
      plan_before: data?.plan ?? null,
    });
    // ── Payments-paused intercept (§9.2) ──
    // switch-annual re-prices with proration, i.e. it CHARGES. Before this it
    // was one of three pay surfaces with no guard at either layer.
    if (
      interceptIfPaymentsPaused({
        kind: "upgrade",
        surface: "annual_upgrade_prompt",
        plan: data?.plan ?? null,
        cadence: "annual",
        label: "Annual billing",
      })
    ) {
      return;
    }
    setSwitching(true);
    setError(null);

    // On Whop an annual switch is a replacement checkout that bills today, so
    // it belongs on our checkout page rather than at a redirect. Before the
    // POST, and provider-gated, for the reasons set out in
    // components/trial/trial-exhausted-modal.tsx — /checkout calls this same
    // route, so deciding after the response would mint the switch twice.
    if (isWhop() && isCustomCheckoutEnabled()) {
      window.location.assign(
        customCheckoutHref({
          type: "switch-annual",
          returnTo: "/settings/billing",
        }),
      );
      return;
    }

    try {
      const res = await fetch("/api/billing/switch-annual", { method: "POST" });
      const result = await res.json();

      if (!res.ok) {
        setError(result.error || "Failed to switch");
        setSwitching(false);
        return;
      }

      // Same rule as the rated flow: on PayPal a 200 means the revision was
      // proposed, not applied — the subscriber approves it on PayPal's site.
      // Showing "Switched!" here told customers they were on annual billing
      // while their subscription was untouched.
      if (typeof result.approval_url === "string" && result.approval_url) {
        window.location.href = result.approval_url;
        return;
      }

      setSwitched(true);
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setSwitching(false);
    }
  }

  return (
    <div className="relative overflow-hidden rounded-xl border border-green-200 bg-gradient-to-r from-green-50 to-emerald-50 p-4 shadow-sm dark:border-green-900/50 dark:from-green-950/30 dark:to-emerald-950/30">
      {/* Dismiss */}
      <button
        onClick={() => setDismissed(true)}
        className="absolute right-3 top-3 rounded-full p-1 text-zinc-400 transition-colors hover:bg-white/50 hover:text-zinc-600 dark:hover:bg-zinc-800/50 dark:hover:text-zinc-300"
      >
        <X className="h-3.5 w-3.5" />
      </button>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-start gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-green-500 to-emerald-500 shadow-md shadow-green-500/20">
            <Gift className="h-4 w-4 text-white" />
          </div>
          <div className="pr-6 sm:pr-0">
            <p className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">
              You&apos;ve been building with Panda for 3 months
            </p>
            <p className="mt-0.5 text-xs text-zinc-600 dark:text-zinc-400">
              Switch to annual and save 2 months &mdash;{" "}
              <span className="font-semibold text-green-600 dark:text-green-400">
                ${data.annualPrice}/yr
              </span>{" "}
              instead of ${yearlyIfMonthly}/yr
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {error && <p className="text-xs text-red-500">{error}</p>}
          <button
            onClick={handleSwitch}
            disabled={switching}
            className="flex shrink-0 items-center gap-1.5 rounded-full bg-gradient-to-r from-green-500 to-emerald-500 px-4 py-2 text-xs font-semibold text-white shadow-md shadow-green-500/20 transition-all hover:brightness-110 disabled:opacity-40"
          >
            {switching ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <>
                Switch to annual
                <ArrowRight className="h-3 w-3" />
              </>
            )}
          </button>
        </div>
      </div>

      {switched && (
        <div className="mt-3 flex items-center gap-2 rounded-lg bg-green-100 px-3 py-2 text-xs font-medium text-green-700 dark:bg-green-900/30 dark:text-green-400">
          <Check className="h-3.5 w-3.5" />
          Switched to annual billing! Your savings start now.
        </div>
      )}
    </div>
  );
}
